import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const UserManagementSection = () => {
  const [users, setUsers] = useState([
    {
      id: 1,
      name: "John Anderson",
      email: "john.anderson@securelock.com",
      role: "admin",
      status: "active",
      lastLogin: new Date(Date.now() - 3600000),
      createdAt: new Date(Date.now() - 86400000 * 30)
    },
    {
      id: 2,
      name: "Sarah Mitchell",
      email: "sarah.mitchell@securelock.com",
      role: "viewer",
      status: "active",
      lastLogin: new Date(Date.now() - 7200000),
      createdAt: new Date(Date.now() - 86400000 * 15)
    },
    {
      id: 3,
      name: "Mike Thompson",
      email: "mike.thompson@securelock.com",
      role: "admin",
      status: "inactive",
      lastLogin: new Date(Date.now() - 86400000 * 7),
      createdAt: new Date(Date.now() - 86400000 * 60)
    }
  ]);

  const [showAddUser, setShowAddUser] = useState(false);
  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    role: 'viewer'
  });
  const [errors, setErrors] = useState({});

  const roleOptions = [
    { value: 'admin', label: 'Administrator', description: 'Full system access and control' },
    { value: 'viewer', label: 'Viewer', description: 'Read-only access to system data' }
  ];

  const validateUser = () => {
    const newErrors = {};
    
    if (!newUser?.name?.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!newUser?.email?.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(newUser?.email)) {
      newErrors.email = 'Please enter a valid email address';
    } else if (users?.some(user => user?.email === newUser?.email)) {
      newErrors.email = 'Email already exists';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleAddUser = () => {
    if (!validateUser()) return;
    
    const user = {
      id: Date.now(),
      ...newUser,
      status: 'active',
      lastLogin: null,
      createdAt: new Date()
    };
    
    setUsers(prev => [...prev, user]);
    setNewUser({ name: '', email: '', role: 'viewer' });
    setShowAddUser(false);
    setErrors({});
  };

  const handleRevokeUser = (userId) => {
    setUsers(prev => prev?.map(user => 
      user?.id === userId 
        ? { ...user, status: user?.status === 'active' ? 'inactive' : 'active' }
        : user
    ));
  };

  const formatDate = (date) => {
    if (!date) return 'Never';
    return date?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getRoleColor = (role) => {
    return role === 'admin' ? 'text-primary' : 'text-accent';
  };

  const getStatusColor = (status) => {
    return status === 'active' ? 'text-success' : 'text-muted-foreground';
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6 security-shadow-card">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-foreground flex items-center space-x-2">
            <Icon name="Users" size={20} className="text-primary" />
            <span>User Management</span>
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            Manage administrator and viewer accounts with role-based permissions
          </p>
        </div>
        <Button
          variant="default"
          iconName="UserPlus"
          iconPosition="left"
          onClick={() => setShowAddUser(true)}
          className="security-transition"
        >
          Add User
        </Button>
      </div>
      {/* Add User Form */}
      {showAddUser && (
        <div className="bg-muted/50 rounded-lg p-4 mb-6 border border-border">
          <h4 className="text-md font-medium text-foreground mb-4">Add New User</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Input
              label="Full Name"
              type="text"
              placeholder="Enter full name"
              value={newUser?.name}
              onChange={(e) => setNewUser(prev => ({ ...prev, name: e?.target?.value }))}
              error={errors?.name}
              required
            />
            <Input
              label="Email Address"
              type="email"
              placeholder="Enter email address"
              value={newUser?.email}
              onChange={(e) => setNewUser(prev => ({ ...prev, email: e?.target?.value }))}
              error={errors?.email}
              required
            />
            <Select
              label="Role"
              options={roleOptions}
              value={newUser?.role}
              onChange={(value) => setNewUser(prev => ({ ...prev, role: value }))}
              required
            />
          </div>
          <div className="flex items-center space-x-3 mt-4">
            <Button
              variant="default"
              iconName="Check"
              iconPosition="left"
              onClick={handleAddUser}
            >
              Add User
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setShowAddUser(false);
                setNewUser({ name: '', email: '', role: 'viewer' });
                setErrors({});
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
      {/* Users Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left py-3 px-2 text-sm font-medium text-muted-foreground">User</th>
              <th className="text-left py-3 px-2 text-sm font-medium text-muted-foreground">Role</th>
              <th className="text-left py-3 px-2 text-sm font-medium text-muted-foreground">Status</th>
              <th className="text-left py-3 px-2 text-sm font-medium text-muted-foreground">Last Login</th>
              <th className="text-left py-3 px-2 text-sm font-medium text-muted-foreground">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users?.map((user) => (
              <tr key={user?.id} className="border-b border-border/50 hover:bg-muted/30 security-transition">
                <td className="py-4 px-2">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-secondary rounded-full flex items-center justify-center">
                      <Icon name="User" size={16} className="text-secondary-foreground" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground">{user?.name}</p>
                      <p className="text-xs text-muted-foreground">{user?.email}</p>
                    </div>
                  </div>
                </td>
                <td className="py-4 px-2">
                  <span className={`text-sm font-medium ${getRoleColor(user?.role)}`}>
                    {user?.role === 'admin' ? 'Administrator' : 'Viewer'}
                  </span>
                </td>
                <td className="py-4 px-2">
                  <div className="flex items-center space-x-2">
                    <div className={`w-2 h-2 rounded-full ${user?.status === 'active' ? 'bg-success' : 'bg-muted-foreground'}`} />
                    <span className={`text-sm ${getStatusColor(user?.status)}`}>
                      {user?.status === 'active' ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </td>
                <td className="py-4 px-2">
                  <span className="text-sm text-muted-foreground">
                    {formatDate(user?.lastLogin)}
                  </span>
                </td>
                <td className="py-4 px-2">
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName={user?.status === 'active' ? 'UserX' : 'UserCheck'}
                      onClick={() => handleRevokeUser(user?.id)}
                      className={user?.status === 'active' ? 'text-error hover:text-error' : 'text-success hover:text-success'}
                    >
                      {user?.status === 'active' ? 'Revoke' : 'Activate'}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="Settings"
                      className="text-muted-foreground hover:text-foreground"
                    >
                      Edit
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {users?.length === 0 && (
        <div className="text-center py-8">
          <Icon name="Users" size={48} className="text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">No users found</p>
        </div>
      )}
    </div>
  );
};

export default UserManagementSection;